import React, { useCallback, useEffect, useState } from 'react'
import { Box, Typography } from '@mui/material'
import UbiquaTowerToastAlert from '../../components/alerts/UbiquaTowerToastAlert'
import LoadingScreen from '../../components/LoadingScreen'
import { RBAC_USER_DETAIL_TEXTS } from './shared/rbacUserDetailConstants'
import { fetchRbacAssignedData } from '../services/rbacApi'
import { Check, Package, Storefront, Users } from '@phosphor-icons/react'
import AppChip from '../../components/chips/AppChip'
import { UI_COLOR_PRIMARY_ALT } from '../../constants/uiColors'
import RbacDetailSection from '../components/RbacDetailSection'
import RbacUserDetailToolbar from '../components/RbacUserDetailToolbar'
import RbacUserSummaryCard from '../components/RbacUserSummaryCard'
import { rbacSectionIconBoxSx } from '../components/constants/rbacDetailCardConstants'
import {
  rbacAssignedItemsSx,
  rbacAssignedAllChipSx,
  rbacAssignedUserChipSx
} from '../components/constants/rbacAssignedDataDetailConstants'
import { APP_USER_ASSIGNMENT_RESOURCES } from '../utils/assignedData'

const RESOURCE_TITLES = {
  products: 'Datos de producto',
  clients: 'Datos de cliente',
  app_users: 'Usuarios de Ubiqua App asignados'
}
const RESOURCE_ICONS = { products: Package, clients: Storefront, app_users: Users }

export default function RbacUserAssignedDataTab({
  user,
  canManage,
  onEditUser,
  onStatusAction,
  onLoadingChange
}) {
  const [state, setState] = useState({ data: null, error: '', loading: true })
  const closeErrorToast = useCallback(() => {
    setState((prev) => ({ ...prev, toastOpen: false }))
  }, [])
  useEffect(() => {
    let cancelled = false
    setState({ data: null, error: '', loading: true })
    fetchRbacAssignedData(user.id)
      .then((data) => {
        if (!cancelled) setState({ data, error: '', loading: false })
      })
      .catch((error) => {
        if (!cancelled)
          setState({
            data: null,
            error: error.message || 'No se pudieron cargar los datos asignados.',
            loading: false,
            toastOpen: true
          })
      })
    return () => {
      cancelled = true
    }
  }, [
    user.id,
    user.updated_at,
    user.supervisor_group,
    user.supervisor_group_product,
    user.supervisor_group_client
  ])
  useEffect(() => {
    onLoadingChange?.(state.loading)
  }, [state.loading, onLoadingChange])

  if (state.loading) return <LoadingScreen text={RBAC_USER_DETAIL_TEXTS.loading} />

  return (
    <Box>
      <RbacUserDetailToolbar
        user={user}
        canManage={canManage}
        onEditUser={onEditUser}
        onStatusAction={onStatusAction}
      />
      <RbacUserSummaryCard user={user} />
      {state.error ? (
        <UbiquaTowerToastAlert
          open={Boolean(state.toastOpen)}
          severity="error"
          title="No se pudieron cargar los datos asignados"
          message={state.error}
          onClose={closeErrorToast}
        />
      ) : (
        APP_USER_ASSIGNMENT_RESOURCES.map((resource) => {
          const value = state.data?.[resource] || { mode: 'none' }
          const uids = value.uids || value.ids || []
          const itemsByUid = new Map(
            (value.items || []).map((item) => [String(item.uid || item.id), item])
          )
          const Icon = RESOURCE_ICONS[resource]
          const allLabel =
            resource === 'products'
              ? 'Todos los productos del catálogo'
              : resource === 'clients'
                ? 'Todos los clientes'
                : 'Todos los usuarios de Ubiqua App'
          return (
            <RbacDetailSection
              key={resource}
              title={RESOURCE_TITLES[resource]}
              icon={
                <Box sx={rbacSectionIconBoxSx}>
                  <Icon size={16} color={UI_COLOR_PRIMARY_ALT} />
                </Box>
              }
              contentSx={{ p: { xs: 2, sm: '18px 28px 16px' } }}
            >
              <Box sx={rbacAssignedItemsSx}>
                {value.mode === 'all' ? (
                  <AppChip
                    label={
                      <>
                        <Check size={18} weight="bold" aria-hidden="true" />
                        <Box component="span">{allLabel}</Box>
                      </>
                    }
                    renderAs="span"
                    sx={rbacAssignedAllChipSx}
                  />
                ) : uids.length ? (
                  uids.map((uid) => {
                    const item = itemsByUid.get(String(uid))
                    return (
                      <AppChip
                        key={uid}
                        label={item?.name ? `${uid} - ${item.name}` : String(uid)}
                        renderAs="span"
                        sx={rbacAssignedUserChipSx}
                      />
                    )
                  })
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Sin valor asignado.
                  </Typography>
                )}
              </Box>
              {value.mode !== 'all' && (
                <Typography
                  component="p"
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.75 }}
                >
                  {value.selection_type === 'exclude'
                    ? `${uids.length} excluido(s); tiene acceso al resto del catálogo.`
                    : resource === 'app_users'
                      ? `${uids.length} usuario(s) asignado(s)`
                      : `${uids.length} asignado(s).`}
                </Typography>
              )}
            </RbacDetailSection>
          )
        })
      )}
    </Box>
  )
}
